/*
 * Merges identical read-only API requests made at almost the same time (e.g. the route guard and
 * the header both asking for the same performer info during one page load). Requests are identical
 * when endpoint, body and Authorization header match. Each caller gets its own copy of `data`, so
 * one caller changing the response cannot affect another. Failures are never reused.
 * Only for read-only endpoints — never for calls that change data (likes, sessions, payments).
 */

export const DEDUPE_WINDOW_MS = 5000;

type Poster = (apiName: string, path: string, init: any) => Promise<any>;

interface Entry { at: number; promise: Promise<any>; }

const entries = new Map<string, Entry>();

export function requestKey(path: string, init: any): string {
  return [path, init?.headers?.Authorization || '', JSON.stringify(init?.body ?? null)].join('|');
}

function copyOf(response: any): any {
  if (!response || typeof response !== 'object' || response.data === undefined) return response;
  return { ...response, data: JSON.parse(JSON.stringify(response.data)) };
}

export function dedupedPost(post: Poster, apiName: string, path: string, init: any, now = Date.now()): Promise<any> {
  const key = requestKey(path, init);
  const hit = entries.get(key);
  if (hit && now - hit.at < DEDUPE_WINDOW_MS) return hit.promise.then(copyOf);
  const promise = post(apiName, path, init);
  entries.set(key, { at: now, promise });
  promise.catch(() => { if (entries.get(key)?.promise === promise) entries.delete(key); });
  return promise.then(copyOf);
}

/** For tests. */
export function clearDedupedRequests(): void {
  entries.clear();
}
